import { countries, type Country } from "@/data/countries";

const STORAGE_KEY = "taskratel:user-coords";
const PROMPT_KEY = "taskratel:location-prompt-shown";

export interface Coords {
  latitude: number;
  longitude: number;
  updatedAt: number;
}

export function getStoredCoords(): Coords | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Coords;
    // Refresh if older than 7 days
    if (Date.now() - parsed.updatedAt > 7 * 24 * 60 * 60 * 1000) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function storeCoords(latitude: number, longitude: number) {
  const coords: Coords = { latitude, longitude, updatedAt: Date.now() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(coords));
  return coords;
}

export function clearStoredCoords() {
  localStorage.removeItem(STORAGE_KEY);
}

export function hasSeenLocationPrompt() {
  return localStorage.getItem(PROMPT_KEY) === "1";
}

export function markLocationPromptShown() {
  localStorage.setItem(PROMPT_KEY, "1");
}

export function requestBrowserLocation(forceFresh = false): Promise<Coords> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation not supported"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = storeCoords(pos.coords.latitude, pos.coords.longitude);
        window.dispatchEvent(new Event("taskratel:coords-updated"));
        resolve(c);
      },
      (err) => reject(err),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: forceFresh ? 0 : 60000 }
    );
  });
}

// Haversine formula — distance in kilometers
export function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const toRad = (v: number) => (v * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export interface ReverseGeocodeResult {
  state?: string;
  county?: string;
  suburb?: string;
  city?: string;
  displayName?: string;
  country?: string;
  countryCode?: string;
}

const GEO_CACHE_KEY = "taskratel:geocode-cache";
const GEO_CACHE_TTL = 24 * 60 * 60 * 1000; // 24h
const GEO_CACHE_MAX = 40;

type GeoCache = Record<string, { at: number; value: ReverseGeocodeResult }>;

// ~500 m precision — nearby refreshes reuse the same cache entry
function cacheKey(lat: number, lon: number) {
  return `${lat.toFixed(3)},${lon.toFixed(3)}`;
}

function readGeoCache(): GeoCache {
  try {
    return JSON.parse(localStorage.getItem(GEO_CACHE_KEY) || "{}") as GeoCache;
  } catch {
    return {};
  }
}

function writeGeoCache(cache: GeoCache) {
  try {
    const entries = Object.entries(cache)
      .sort((a, b) => b[1].at - a[1].at)
      .slice(0, GEO_CACHE_MAX);
    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    /* storage full — ignore */
  }
}

// In-flight de-duplication so concurrent callers share one network request
const inFlight = new Map<string, Promise<ReverseGeocodeResult>>();

// Forward geocode a city/area name to approximate coordinates (Nominatim).
// Used as a fallback when GPS permission is denied and no stored fix exists.
export async function forwardGeocode(query: string): Promise<{ latitude: number; longitude: number } | null> {
  const q = query.trim();
  if (!q) return null;
  const key = `fwd:${q.toLowerCase()}`;
  const cache = readGeoCache();
  const hit = cache[key];
  if (hit && Date.now() - hit.at < GEO_CACHE_TTL && (hit.value as { lat?: number }).lat !== undefined) {
    const v = hit.value as unknown as { lat: number; lon: number };
    return { latitude: v.lat, longitude: v.lon };
  }
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=1&q=${encodeURIComponent(q)}`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) return null;
    const data = (await res.json()) as Array<{ lat: string; lon: string }>;
    if (!data.length) return null;
    const value = { lat: parseFloat(data[0].lat), lon: parseFloat(data[0].lon) };
    const fresh = readGeoCache();
    fresh[key] = { at: Date.now(), value: value as unknown as ReverseGeocodeResult };
    writeGeoCache(fresh);
    return { latitude: value.lat, longitude: value.lon };
  } catch {
    return null;
  }
}

// Reverse geocode via OpenStreetMap Nominatim (no API key required), cached locally
export async function reverseGeocode(lat: number, lon: number): Promise<ReverseGeocodeResult> {
  const key = cacheKey(lat, lon);
  const cache = readGeoCache();
  const hit = cache[key];
  if (hit && Date.now() - hit.at < GEO_CACHE_TTL) return hit.value;

  const existing = inFlight.get(key);
  if (existing) return existing;

  const req = (async () => {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=14&addressdetails=1`;
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) {
      // Fall back to a stale cache entry rather than failing outright
      if (hit) return hit.value;
      throw new Error("Reverse geocoding failed");
    }
    const data = await res.json();
    const a = data.address || {};
    const value: ReverseGeocodeResult = {
      state: a.state,
      county: a.county || a.local_government_area,
      suburb: a.suburb || a.neighbourhood || a.quarter,
      city: a.city || a.town || a.village,
      country: a.country,
      countryCode: (a.country_code || "").toUpperCase() || undefined,
      displayName: data.display_name,
    };
    const fresh = readGeoCache();
    fresh[key] = { at: Date.now(), value };
    writeGeoCache(fresh);
    return value;
  })().finally(() => inFlight.delete(key));

  inFlight.set(key, req);
  return req;
}


function normalize(s: string) {
  return s.toLowerCase().replace(/\s*state\s*$/i, "").replace(/[^a-z0-9]/g, "");
}

export interface LocationMatch {
  countryId?: string;
  stateId?: string;
  lgaId?: string;
  area?: string;
}

const COUNTRY_NAME_ALIASES: Record<string, string> = {
  benin: "beninrepublic",
};

function resolveCountry(result: ReverseGeocodeResult): Country | undefined {
  if (result.countryCode) {
    const byCode = countries.find((c) => c.code === result.countryCode);
    if (byCode) return byCode;
  }
  if (result.country) {
    const norm = normalize(result.country);
    const aliasNorm = COUNTRY_NAME_ALIASES[norm] ?? norm;
    const byName = countries.find((c) => normalize(c.name) === aliasNorm || normalize(c.name) === norm);
    if (byName) return byName;
  }
  return undefined;
}

// Map a Nominatim response to our country -> region -> area hierarchy
export function matchLocation(result: ReverseGeocodeResult): LocationMatch {
  const match: LocationMatch = {};
  const country = resolveCountry(result);
  if (country) match.countryId = country.id;

  const regionCandidates = [result.state, result.county, result.city].filter(Boolean) as string[];
  if (regionCandidates.length === 0) return match;

  const pool = country ? country.states : countries.flatMap((c) => c.states);
  let state = undefined as (typeof pool)[number] | undefined;
  for (const cand of regionCandidates) {
    const n = normalize(cand);
    state = pool.find(
      (s) => normalize(s.name) === n || normalize(s.id) === n || normalize(s.name).includes(n) || n.includes(normalize(s.name)),
    );
    if (state) break;
  }
  if (!state) return match;
  match.stateId = state.id;
  if (!match.countryId) {
    match.countryId = countries.find((c) => c.states.some((s) => s.id === state!.id))?.id;
  }

  const countyCandidates = [result.county, result.city, result.suburb].filter(Boolean) as string[];
  for (const cand of countyCandidates) {
    const norm = normalize(cand);
    const lga = state.lgas.find(
      (l) => normalize(l.name) === norm || norm.includes(normalize(l.name)) || normalize(l.name).includes(norm)
    );
    if (lga) {
      match.lgaId = lga.id;
      break;
    }
  }

  const areaCandidates = [result.suburb, result.city].filter(Boolean) as string[];
  if (match.lgaId) {
    const lga = state.lgas.find((l) => l.id === match.lgaId);
    for (const cand of areaCandidates) {
      const norm = normalize(cand);
      const area = lga?.areas.find(
        (a) => normalize(a.name) === norm || norm.includes(normalize(a.name))
      );
      if (area) {
        match.area = area.name;
        break;
      }
    }
    // Fall back to raw suburb name if no exact match
    if (!match.area && areaCandidates[0]) match.area = areaCandidates[0];
  }

  return match;
}

/** @deprecated use matchLocation — kept for backwards compatibility */
export const matchNigeriaLocation = matchLocation;

export function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m away`;
  if (km < 10) return `${km.toFixed(1)} km away`;
  return `${Math.round(km)} km away`;
}